import type { useTaskDetail } from '../../../entities/task';
import type { useWorkOrderDetail } from '../../../entities/work-order';
import { workOrderAddress } from '../../../shared/helpers/address';
import { formatDateDDMMYYYY } from '../../../shared/helpers/date';
import type { DispositionMode } from './useDispositionMode';

type WorkOrderDetail = ReturnType<typeof useWorkOrderDetail>['data'];
type TaskDetail = ReturnType<typeof useTaskDetail>['data'];

const dayFormatter = new Intl.DateTimeFormat('de-DE', {
  weekday: 'short',
  day: '2-digit',
  month: '2-digit',
});

/** The one line that says what is being planned. */
export function planSummary(
  plan: DispositionMode,
  workOrder: WorkOrderDetail,
  task: TaskDetail,
): string {
  if (plan.mode === 'schedule-appointment') {
    const external = workOrder?.external;
    // Rescheduling has no `work-order-id` in the URL — the Auftrag is only known once the
    // appointment has loaded, so name the Termin alone until then.
    if (!external) {
      return plan.workOrderId ? `Termin · Auftrag ${plan.workOrderId}` : 'Termin';
    }

    // Same fields the block tooltip uses, so the bar and the board name a job the same way.
    return [
      `Nr. ${external.auftragJahr}/${external.auftragNr}`,
      workOrderAddress(external),
      external.gewerkeText,
    ]
      .filter(Boolean)
      .join(' · ');
  }

  if (plan.taskId == null) return 'Neue Aufgabe';
  if (!task) return 'Aufgabe';

  return [task.title, task.deadline ? `Fällig ${formatDateDDMMYYYY(task.deadline)}` : null]
    .filter(Boolean)
    .join(' · ');
}

/** `Mo, 11.08.` for the planning bar's location line. */
export function formatPlanDay(dayIso: string): string {
  const date = new Date(`${dayIso}T00:00:00`);
  return Number.isNaN(date.getTime()) ? dayIso : dayFormatter.format(date);
}
